import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, CircularProgress, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  IconButton, Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  People as PeopleIcon, Payments as PayrollIcon,
  PersonAdd as NewHireIcon, PauseCircleOutline as InactiveIcon,
  Groups as DeptIcon, Redeem as AllowanceIcon,
  AccountBalanceWallet as BasicIcon, WorkOutline as TypeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import ModuleHeader from '../../components/ModuleHeader';

const fmt = (n) => Number(n || 0).toLocaleString('en-GH',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoney = (n, ccy = 'GHS') => `${ccy} ${fmt(n)}`;

// Compact left-bordered stat tile. Used along the top strip.
function StatTile({ icon, label, value, tone = 'primary', sub }) {
  return (
    <Card sx={{ flex: 1, minWidth: 0, borderRadius: 2,
      borderLeft: 4, borderColor: `${tone}.main`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: (t) => alpha(t.palette[tone].main, 0.12),
          color: `${tone}.main`,
        }}>{icon}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.5,
                  textTransform: 'uppercase', fontSize: 10, lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2 }} noWrap>
            {value}
          </Typography>
          {sub && <Typography variant="caption" color="text.secondary" noWrap>{sub}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

// Shared PRO_TABLE styling — keeps the dashboard tables consistent
// with the rest of the system (uppercase headers, zebra, hover).
const PRO_TABLE = {
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 800, fontSize: 10.5, letterSpacing: 0.6,
    textTransform: 'uppercase', color: 'text.secondary',
    bgcolor: '#f7f9fc', py: 1, borderBottom: '1px solid #d9dee6',
  },
  '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)':
    { backgroundColor: '#fafbfc' },
  '& .MuiTableBody-root .MuiTableRow-root:hover':
    { backgroundColor: 'rgba(25,118,210,0.06) !important' },
  '& .MuiTableBody-root .MuiTableCell-root': {
    fontSize: 12.5, py: 1.05, borderBottom: '1px solid #eef0f4',
    fontVariantNumeric: 'tabular-nums',
  },
};

function BreakdownTable({ rows, totals, currency, firstColLabel, firstColKey }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
      <Table size="small" sx={PRO_TABLE}>
        <TableHead>
          <TableRow>
            <TableCell>{firstColLabel}</TableCell>
            <TableCell align="right">Headcount</TableCell>
            <TableCell align="right">Basic</TableCell>
            <TableCell align="right">Allowances</TableCell>
            <TableCell align="right">Monthly gross</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={5} align="center"
              sx={{ color: 'text.secondary', fontStyle: 'italic', py: 3 }}>
              No data yet.
            </TableCell></TableRow>
          )}
          {rows.map(r => (
            <TableRow key={r[firstColKey]} hover>
              <TableCell sx={{ fontWeight: 700 }}>{r[firstColKey]}</TableCell>
              <TableCell align="right">{r.headcount}</TableCell>
              <TableCell align="right">{fmt(r.basic_total)}</TableCell>
              <TableCell align="right">{fmt(r.allowances_total)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(r.monthly_gross, currency)}</TableCell>
            </TableRow>
          ))}
          {rows.length > 0 && (
            <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', fontWeight: 800, bgcolor: '#f7f9fc' } }}>
              <TableCell>TOTAL</TableCell>
              <TableCell align="right">{totals.headcount}</TableCell>
              <TableCell align="right">{fmt(totals.basic)}</TableCell>
              <TableCell align="right">{fmt(totals.allowances)}</TableCell>
              <TableCell align="right">{fmtMoney(totals.gross, currency)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function HRDashboardPage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const r = await API.get('/hr/summary');
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load HR summary');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const currency = useMemo(() => data?.currency || 'GHS', [data]);

  // Aggregate totals row for each breakdown table.
  const deptTotals = useMemo(() => {
    if (!data?.by_department) return { headcount: 0, basic: 0, allowances: 0, gross: 0 };
    return data.by_department.reduce(
      (a, r) => ({
        headcount: a.headcount + Number(r.headcount),
        basic:     a.basic     + Number(r.basic_total),
        allowances:a.allowances+ Number(r.allowances_total),
        gross:     a.gross     + Number(r.monthly_gross),
      }),
      { headcount: 0, basic: 0, allowances: 0, gross: 0 }
    );
  }, [data]);
  const typeTotals = useMemo(() => {
    if (!data?.by_type) return { headcount: 0, basic: 0, allowances: 0, gross: 0 };
    return data.by_type.reduce(
      (a, r) => ({
        headcount: a.headcount + Number(r.headcount),
        basic:     a.basic     + Number(r.basic_total),
        allowances:a.allowances+ Number(r.allowances_total),
        gross:     a.gross     + Number(r.monthly_gross),
      }),
      { headcount: 0, basic: 0, allowances: 0, gross: 0 }
    );
  }, [data]);

  if (loading) return (
    <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
  );
  if (error) return (
    <Typography color="error" sx={{ p: 3 }}>{error}</Typography>
  );

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto' }}>
      <ModuleHeader
        title="HR Dashboard"
        subtitle="Headcount, salary exposure and allowance overview across the school"
      />

      <Stack direction="row" sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      {/* ── Top strip — 6 KPI tiles ──────────────────────────── */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} sx={{ mb: 2 }}>
        <StatTile icon={<PeopleIcon />}   tone="primary" label="Active employees"    value={data.active}
          sub={`${data.total} total · ${data.inactive} inactive`} />
        <StatTile icon={<NewHireIcon />}  tone="success" label="New hires (this month)" value={data.new_hires_this_month} />
        <StatTile icon={<InactiveIcon />} tone="error"   label="Inactive / past staff"  value={data.inactive} />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} sx={{ mb: 3 }}>
        <StatTile icon={<BasicIcon />}     tone="info"    label="Monthly basic salaries"
          value={fmtMoney(data.basic_total, currency)} />
        <StatTile icon={<AllowanceIcon />} tone="warning" label="Monthly allowances"
          value={fmtMoney(data.allowances_total, currency)} />
        <StatTile icon={<PayrollIcon />}   tone="success" label="Monthly payroll exposure"
          value={fmtMoney(data.monthly_gross, currency)}
          sub="Basic + allowances (before SSNIT / PAYE)" />
      </Stack>

      {/* ── By department ────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <DeptIcon color="primary" />
            <Typography fontWeight={800}>By department</Typography>
            <Box sx={{ flex: 1 }} />
            <Chip size="small" variant="outlined" label={`${data.by_department.length} dept(s)`} />
          </Stack>
          <BreakdownTable rows={data.by_department} totals={deptTotals}
            currency={currency} firstColLabel="Department" firstColKey="department" />
        </CardContent>
      </Card>

      {/* ── By employment type ───────────────────────────────── */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <TypeIcon color="secondary" />
            <Typography fontWeight={800}>By employment type</Typography>
            <Box sx={{ flex: 1 }} />
            <Chip size="small" variant="outlined" label={`${data.by_type.length} type(s)`} />
          </Stack>
          <BreakdownTable rows={data.by_type} totals={typeTotals}
            currency={currency} firstColLabel="Type" firstColKey="employment_type" />
        </CardContent>
      </Card>
    </Box>
  );
}
